import { axiosGet } from '@/api/handlers';
import { useQuery } from '@/api/hooks';
import { useFeatureFlags } from '@/features/feature-flag/_hooks/feature-flags';
import {
  type TagListViewableSchema,
  TagTypeLabelModifier,
  TagsModel,
  tagType,
} from '@/features/tag/models';
import { queryKeyMap } from '@/features/tag/utils';
import type { z } from 'zod';

export const useTagOptions = ({
  params,
  type,
  enabled,
}: {
  params?: z.infer<typeof TagListViewableSchema>;
  type: z.infer<typeof tagType> | 'all';
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.tags(params),
    queryFn: async () => {
      const res = await axiosGet('/api/tags/list_viewable/', params);
      return TagsModel.parse(res);
    },
    select: (data) =>
      data.flatMap((d) =>
        d.type === type || type === 'all'
          ? {
              value: d.id,
              label: d.name,
              scope: d.scope,
              isTaggable: d.isTaggable,
            }
          : [],
      ),
    enabled,
  });
};

export const useTagTypeOptions = () => {
  const { features } = useFeatureFlags();

  const tagTypeOptions = tagType.options.flatMap((value) => {
    const isCommentDisabled = !features?.comment && value === 'comment';
    const isWorkflowDisabled = !features?.workflow && value === 'workflow';

    if (!['comment', 'workflow'].includes(value)) {
      return [];
    }

    if (isCommentDisabled || isWorkflowDisabled) {
      return [];
    }

    return {
      label: TagTypeLabelModifier[value],
      value,
    };
  });

  return { tagTypeOptions };
};
